/**
 * V2CarouselSection — V2-native editor section for step.content.carousel
 *
 * UI is based on the retired OnboardingSliderSection, with V2 data paths.
 * Reuses V2-owned shared components: ItemTypeSelector, SliderItemEditor, PlayPauseButton,
 * AddSlideButton, DeleteItemDialog, DurationRangeSlider.
 *
 * V2 paths: content.carousel.*
 * Replaces: OnboardingSliderSection (for V2 context)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, TextField, Collapse, Divider } from '@mui/material';
import { SAccordion, SortableList } from '@modules/ui-components';
import { normalizeSlideItems } from '@modules/screen-view-v2';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import {
  V2_CAROUSEL_KIND_OPTIONS,
  DEFAULT_TIMER_DURATION,
  createDefaultV2Slide,
  slidesToDurations,
  durationsToSlideRanges,
  getCarouselVisibility,
  createV2ContainerSections,
} from '../../../config/v2CarouselConfig';
import { createV2CarouselStyleSections } from '../../../config/v2CarouselStyleConfig';
import { PlayPauseButton, AddSlideButton } from './shared/SliderControls';
import { SliderItemEditor } from './shared/SliderItemEditor';
import { ItemTypeSelector } from './shared/ItemTypeSelector';
import { DeleteItemDialog } from './shared/DeleteItemDialog';
import { V2ListStyleSections } from './list/V2ListStyleSections';
import { TiptapTemplateEditor, type LabelBlockType } from '../../shared/templateFields/TiptapTemplateEditor';
import { buildFlowVariableItems } from '../../../config/flowVariablesConfig';
import { variableResolver } from '@modules/v2-variable-utils';
import {
  redistributeAfterAdd,
  redistributeAfterRemove,
  distributeDurationsEvenly,
  calculateRanges,
  handleRangeChange,
  canAddSlide,
  canRemoveSlide,
} from '../../../utils/durationDistribution';
import { getSectionTestId } from '../../../utils/testIdUtils';
import { getValueByPath, getV2TranslationValue } from '../../../utils/v2FieldOperations';
import {
  cloneTemplateItem,
  resetAssetTranslations,
  resetLocalizedLabelContent,
} from '@modules/reactFlow/utils/v2ItemInheritance';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FlowDocument, InlineCountdownConfig } from '@segmently/schema-models/v2';
import type { FunnelVariable } from '@modules/v2-variable-utils';
import type { SliderTextFieldRenderProps } from '../../../types/sliderSection.types';

interface SlideItem {
  progressStart: number;
  progressEnd: number;
  content: Record<string, unknown>;
}

interface EditableSlide {
  id: string;
  originalIndex: number;
  title: string;
  subtitle1: string;
  subtitle2: string;
  imageUrl: string;
  durationPercent: number;
  hasConnections: boolean;
}

type CarouselTemplateField = 'title' | 'subtitle' | 'detail';

export interface V2CarouselSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  onPreviewPlay?: () => void;
  onPreviewStop?: () => void;
  isPreviewPlaying?: boolean;
  /** Full FlowDocument for BFS-based variable resolution in slide text @ menu */
  flowDocument?: FlowDocument | null;
}

export const V2CarouselSection: React.FC<V2CarouselSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  onPreviewPlay,
  onPreviewStop,
  isPreviewPlaying = false,
  flowDocument,
}) => {
  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);
  const lastUploadedRef = useRef<{ original: string; small: string } | null>(null);

  // State for styles section expansion (gear icon toggle)
  const [stylesExpanded, setStylesExpanded] = useState(false);

  // State for delete confirmation dialog
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    itemIndex: number;
    itemTitle: string;
    hasConnections: boolean;
  }>({ open: false, itemIndex: -1, itemTitle: '', hasConnections: false });

  const carouselStyleSections = useMemo(() => createV2CarouselStyleSections(), []);
  const containerSections = useMemo(() => createV2ContainerSections(), []);

  const flowVariableItems = useMemo(() => {
    if (!flowDocument || !step.id) return [];

    const boundVars = variableResolver.findAvailableVariables(flowDocument, step.id);
    const varsRecord: Record<string, FunnelVariable> = {};
    for (const boundVar of boundVars) {
      varsRecord[boundVar.variable.id] = boundVar.variable;
    }

    return buildFlowVariableItems(varsRecord);
  }, [flowDocument, step.id]);

  // Read carousel config
  const kind = useMemo(
    () => getValueByPath<string>(step, 'content.carousel.kind') ?? 'visualCaptionDetailExtra',
    [step]
  );

  const timerDuration = useMemo(
    () => getValueByPath<number>(step, 'content.carousel.timer.duration') ?? DEFAULT_TIMER_DURATION,
    [step]
  );

  const rawItems = useMemo(() => {
    const items = getValueByPath<unknown>(step, 'content.carousel.items');
    return normalizeSlideItems(items) as unknown as SlideItem[];
  }, [step]);

  // Convert to editable slides with duration percentages
  const editableSlides: EditableSlide[] = useMemo(() => {
    const durations = rawItems.length > 0
      ? slidesToDurations(rawItems)
      : distributeDurationsEvenly(rawItems.length);

    const sum = durations.reduce((s, d) => s + d, 0);
    const normalizedDurations = Math.abs(sum - 100) < 1 ? durations : distributeDurationsEvenly(rawItems.length);

    return rawItems.map((_, index) => ({
      id: `slide-${index}`,
      originalIndex: index,
      title: getV2TranslationValue(step, `content.carousel.items.${index}.content.title`, locale),
      subtitle1: getV2TranslationValue(step, `content.carousel.items.${index}.content.subtitle`, locale),
      subtitle2: getV2TranslationValue(step, `content.carousel.items.${index}.content.detail`, locale),
      imageUrl: (() => {
        const asset = getValueByPath<Record<string, string>>(
          step, `content.carousel.items.${index}.content.image.translations.${locale}`
        );
        if (!asset) return '';
        if (typeof asset === 'string') return asset;
        return asset.original ?? asset.origin ?? '';
      })(),
      durationPercent: normalizedDurations[index] ?? 0,
      hasConnections: false,
    }));
  }, [step, rawItems, locale]);

  // Get visibility config for current kind
  const visibility = useMemo(() => getCarouselVisibility(kind), [kind]);

  // Calculate ranges for two-thumb sliders
  const ranges = useMemo(() => {
    const durations = editableSlides.map((item) => item.durationPercent);
    return calculateRanges(durations);
  }, [editableSlides]);

  // ============================================
  // Handlers
  // ============================================

  const handleKindChange = useCallback(
    (newKind: string) => {
      onChange('content.carousel.kind', newKind);
    },
    [onChange]
  );

  const handleStylesClick = useCallback(() => {
    setStylesExpanded((prev) => !prev);
  }, []);

  const handleTimerDurationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);
      if (!isNaN(value) && value >= 0.1 && value <= 120) {
        onChange('content.carousel.timer.duration', value);
      }
    },
    [onChange]
  );

  const handlePlay = useCallback(() => {
    if (onPreviewPlay) onPreviewPlay();
  }, [onPreviewPlay]);

  const handleStop = useCallback(() => {
    if (onPreviewStop) onPreviewStop();
  }, [onPreviewStop]);

  // ============================================
  // Range slider handlers
  // ============================================

  const handleItemRangeChange = useCallback(
    (index: number, newRange: [number, number]) => {
      const currentDurations = editableSlides.map((item) => item.durationPercent);
      const newDurations = handleRangeChange(currentDurations, index, newRange);
      const newRanges = durationsToSlideRanges(newDurations);

      const newItems = rawItems.map((item, idx) => ({
        ...item,
        progressStart: newRanges[idx].progressStart,
        progressEnd: newRanges[idx].progressEnd,
      }));

      onChange('content.carousel.items', newItems);
    },
    [editableSlides, rawItems, onChange]
  );

  // ============================================
  // Item content handlers
  // ============================================

  const handleItemTitleChange = useCallback(
    (index: number, value: string) => {
      onChange(`content.carousel.items.${index}.content.title.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const handleItemSubtitle1Change = useCallback(
    (index: number, value: string) => {
      onChange(`content.carousel.items.${index}.content.subtitle.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const handleItemSubtitle2Change = useCallback(
    (index: number, value: string) => {
      onChange(`content.carousel.items.${index}.content.detail.translations.${locale}`, value);
    },
    [onChange, locale]
  );

  const ensureItemTextDynamicKind = useCallback(
    (basePath: string) => {
      const currentKind = getValueByPath<string>(step, `${basePath}.kind`) || 'static';
      if (currentKind !== 'dynamic') {
        onChange(`${basePath}.kind`, 'dynamic');
      }
    },
    [step, onChange]
  );

  const renderTemplateTextField = useCallback(
    (index: number, fieldName: CarouselTemplateField, props: SliderTextFieldRenderProps) => {
      const basePath = `content.carousel.items.${index}.content.${fieldName}`;
      const labels = getValueByPath<Record<string, LabelBlockType>>(
        step,
        `${basePath}.templateParams.labels`
      ) || {};
      const links = getValueByPath<Record<string, string>>(
        step,
        `${basePath}.templateParams.links`
      ) || {};
      const countdowns = getValueByPath<Record<string, InlineCountdownConfig>>(
        step,
        `${basePath}.templateParams.countdowns`
      ) || {};
      const parentTextStyles = getValueByPath<Partial<LabelBlockType>>(
        step,
        `${basePath}.appearance`
      ) || {};

      const updateText = (text: string) => {
        props.onChange(text);
      };

      return (
        <TiptapTemplateEditor
          value={props.value}
          onChange={updateText}
          disabled={props.disabled}
          placeholder={props.placeholder}
          existingLinks={links}
          existingLabels={labels}
          existingCountdowns={countdowns}
          onAddLink={(name, url, updatedText) => {
            if (updatedText !== undefined) updateText(updatedText);
            onChange(`${basePath}.templateParams.links.${name}`, url);
            ensureItemTextDynamicKind(basePath);
          }}
          onAddLabel={(name, labelBlock, updatedText) => {
            if (updatedText !== undefined) updateText(updatedText);
            onChange(`${basePath}.templateParams.labels.${name}`, labelBlock);
            ensureItemTextDynamicKind(basePath);
          }}
          onAddCountdown={(name, config, updatedText) => {
            if (updatedText !== undefined) updateText(updatedText);
            onChange(`${basePath}.templateParams.countdowns.${name}`, config);
            ensureItemTextDynamicKind(basePath);
          }}
          onDeleteLink={(name, updatedText) => {
            updateText(updatedText);
            onChange(`${basePath}.templateParams.links.${name}`, null);
          }}
          onDeleteLabel={(name, updatedText) => {
            updateText(updatedText);
            onChange(`${basePath}.templateParams.labels.${name}`, null);
          }}
          onDeleteCountdown={(name, updatedText) => {
            updateText(updatedText);
            onChange(`${basePath}.templateParams.countdowns.${name}`, null);
          }}
          parentTextStyles={parentTextStyles}
          screenType={step.screenType}
          flowVariables={flowVariableItems}
          data-testid={props.testId}
        />
      );
    },
    [step, onChange, ensureItemTextDynamicKind, flowVariableItems]
  );

  const handleItemImageChange = useCallback(
    (index: number, value: string | null) => {
      if (value) {
        const asset = lastUploadedRef.current ?? { original: value, small: value };
        onChange(`content.carousel.items.${index}.content.image.translations.${locale}`, asset);
        lastUploadedRef.current = null;
      } else {
        onChange(`content.carousel.items.${index}.content.image.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      const result = await uploadService.uploadImage(file, { projectId: projectId!, onboardingId: onboardingId!, versionId: versionId!, locale });
      lastUploadedRef.current = { original: result.assetUrl.origin, small: result.assetUrl.small };
      return result.assetUrl.origin;
    },
    [uploadService, projectId, onboardingId, versionId, locale]
  );

  // ============================================
  // Add/Delete/Reorder handlers
  // ============================================

  const handleAddItem = useCallback(() => {
    if (!canAddSlide(rawItems.length)) return;

    const currentDurations = editableSlides.map((s) => s.durationPercent);
    const newDurations = redistributeAfterAdd(currentDurations);
    const newRanges = durationsToSlideRanges(newDurations);

    const newRange = newRanges[newRanges.length - 1];
    const newSlide = cloneTemplateItem<Record<string, unknown>>(rawItems) ?? createDefaultV2Slide(
      locale,
      newRange.progressStart,
      newRange.progressEnd
    );

    newSlide.progressStart = newRange.progressStart;
    newSlide.progressEnd = newRange.progressEnd;

    const content = (newSlide.content && typeof newSlide.content === 'object'
      ? newSlide.content
      : {}) as Record<string, unknown>;
    content.title = resetLocalizedLabelContent(content.title, locale);
    content.subtitle = resetLocalizedLabelContent(content.subtitle, locale);
    content.detail = resetLocalizedLabelContent(content.detail ?? content.subtitle2, locale);
    delete content.subtitle2;
    content.image = resetAssetTranslations(content.image);
    newSlide.content = content;

    const updatedItems = rawItems.map((item, i) => ({
      ...item,
      progressStart: newRanges[i].progressStart,
      progressEnd: newRanges[i].progressEnd,
    }));
    updatedItems.push(newSlide as (typeof updatedItems)[number]);

    onChange('content.carousel.items', updatedItems);
  }, [editableSlides, rawItems, locale, onChange]);

  const handleDeleteRequest = useCallback(
    (index: number) => {
      const item = editableSlides.find((i) => i.originalIndex === index);
      if (!item) return;

      setDeleteDialogState({
        open: true,
        itemIndex: index,
        itemTitle: item.title || `Slide ${index + 1}`,
        hasConnections: false,
      });
    },
    [editableSlides]
  );

  const handleDeleteConfirm = useCallback(() => {
    const { itemIndex } = deleteDialogState;
    const currentDurations = editableSlides.map((s) => s.durationPercent);
    const newDurations = redistributeAfterRemove(currentDurations, itemIndex);
    const newRanges = durationsToSlideRanges(newDurations);

    const newItems = rawItems
      .filter((_, i) => i !== itemIndex)
      .map((item, i) => ({
        ...item,
        progressStart: newRanges[i].progressStart,
        progressEnd: newRanges[i].progressEnd,
      }));

    onChange('content.carousel.items', newItems);
    setDeleteDialogState({ open: false, itemIndex: -1, itemTitle: '', hasConnections: false });
  }, [deleteDialogState, editableSlides, rawItems, onChange]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogState({ open: false, itemIndex: -1, itemTitle: '', hasConnections: false });
  }, []);

  const handleReorder = useCallback(
    (reorderedItems: EditableSlide[]) => {
      const newRawItems = reorderedItems.map((item) => rawItems[item.originalIndex]);
      onChange('content.carousel.items', newRawItems);
    },
    [rawItems, onChange]
  );

  // ============================================
  // Computed values
  // ============================================

  const canAdd = canAddSlide(editableSlides.length);
  const canDelete = canRemoveSlide(editableSlides.length);

  const SECTION_NAME = 'Carousel';

  return (
    <SAccordion
      title="Carousel"
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      {/* Item Type Selector + Styles Gear Icon */}
      <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
        <ItemTypeSelector
          value={kind}
          options={V2_CAROUSEL_KIND_OPTIONS}
          getVisibility={getCarouselVisibility}
          onChange={handleKindChange}
          onStylesClick={handleStylesClick}
          disabled={disabled}
          stylesExpanded={stylesExpanded}
          testIdPrefix="carousel"
          label="Slide Type"
        />
      </Box>

      {/* Style Sections (collapsible via gear icon) */}
      <Collapse in={stylesExpanded} timeout="auto">
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }} data-testid="carousel-style-sections-container">
          <V2ListStyleSections
            sections={carouselStyleSections}
            currentItemType={kind}
            step={step}
            onChange={onChange}
            disabled={disabled}
            itemsBasePath="content.carousel.items"
          />

          {/* Image/Text Container layout settings */}
          <Box sx={{ mt: 1 }} data-testid="carousel-container-sections">
            <V2ListStyleSections
              sections={containerSections}
              currentItemType={kind}
              step={step}
              onChange={onChange}
              disabled={disabled}
            />
          </Box>
        </Box>
      </Collapse>

      {/* Carousel progress indicator settings are temporarily hidden until carousel-specific indicator UX is finalized. */}

      {/* Timer Duration + Play Button Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          mb: FIELD_MARGIN_BOTTOM,
        }}
        data-testid="carousel-timer-container"
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Total Duration (seconds)
          </Typography>
          <TextField
            data-testid="carousel-timer-input"
            type="number"
            size="small"
            value={timerDuration}
            onChange={handleTimerDurationChange}
            disabled={disabled}
            fullWidth
            inputProps={{
              min: 0.1,
              max: 120,
              step: 0.1,
            }}
          />
        </Box>
        <PlayPauseButton
          onPlay={handlePlay}
          onPause={handleStop}
          isPlaying={isPreviewPlaying}
          disabled={disabled}
        />
      </Box>

      {/* Add Slide Button - aligned right */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <AddSlideButton
          onAdd={handleAddItem}
          canAddMore={canAdd}
          disabled={disabled}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Items List with drag-and-drop */}
      <Box data-testid="carousel-items-container">
        {editableSlides.length > 0 ? (
          <SortableList
            items={editableSlides}
            onReorder={handleReorder}
            gap="sm"
            disabled={disabled}
          >
            {(slide: EditableSlide, index: number) => (
              <SortableList.Item
                key={slide.id}
                showDragHandle
                dragHandlePosition="start"
                disabled={disabled}
              >
                <SliderItemEditor
                  index={slide.originalIndex}
                  title={slide.title}
                  subtitle1={slide.subtitle1}
                  subtitle2={slide.subtitle2}
                  imageUrl={slide.imageUrl}
                  durationPercent={slide.durationPercent}
                  range={ranges[index] || [0, 100]}
                  totalDurationSeconds={timerDuration}
                  isFirst={index === 0}
                  isLast={index === editableSlides.length - 1}
                  hasConnections={slide.hasConnections}
                  visibility={visibility}
                  onTitleChange={(value) => handleItemTitleChange(slide.originalIndex, value)}
                  onSubtitle1Change={(value) => handleItemSubtitle1Change(slide.originalIndex, value)}
                  onSubtitle2Change={(value) => handleItemSubtitle2Change(slide.originalIndex, value)}
                  onImageChange={(value) => handleItemImageChange(slide.originalIndex, value)}
                  onImageUpload={handleImageUpload}
                  onRangeChange={(newRange) => handleItemRangeChange(index, newRange)}
                  onDelete={() => handleDeleteRequest(slide.originalIndex)}
                  canDelete={canDelete}
                  disabled={disabled}
                  renderTitleField={(props) => renderTemplateTextField(slide.originalIndex, 'title', props)}
                  renderSubtitle1Field={(props) => renderTemplateTextField(slide.originalIndex, 'subtitle', props)}
                  renderSubtitle2Field={(props) => renderTemplateTextField(slide.originalIndex, 'detail', props)}
                />
              </SortableList.Item>
            )}
          </SortableList>
        ) : (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No slides. Click &quot;Add Slide&quot; to create one.
          </Typography>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <DeleteItemDialog
        open={deleteDialogState.open}
        itemIndex={deleteDialogState.itemIndex}
        itemTitle={deleteDialogState.itemTitle}
        hasConnections={deleteDialogState.hasConnections}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </SAccordion>
  );
};
